import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Building2, CheckCircle, Download, ArrowRight } from 'lucide-react';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const { transactionId, plan, email } = location.state || {};

  useEffect(() => {
    if (!transactionId || !plan) {
      navigate('/choose-plan');
    }
  }, [transactionId, plan, navigate]);

  if (!plan) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-cyan-50 to-orange-50 p-4">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl mb-4 shadow-lg">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
            MyVenueMate
          </h1>
        </div>

        <Card className="shadow-xl border-0">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 dark:text-foreground mb-4">
              Payment Successful!
            </h2>
            
            <p className="text-gray-600 dark:text-muted-foreground mb-6 max-w-md mx-auto">
              Your subscription to the <strong>{plan.name} Plan</strong> has been activated successfully.
            </p>

            <div className="bg-teal-50 dark:bg-teal-900/20 border-2 border-teal-200 rounded-lg p-6 mb-6">
              <div className="grid grid-cols-2 gap-4 text-left">
                <div>
                  <p className="text-sm text-gray-600 dark:text-muted-foreground">Transaction ID</p>
                  <p className="font-mono text-sm font-medium text-gray-900 dark:text-foreground break-all">{transactionId}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-muted-foreground">Plan</p>
                  <p className="font-medium text-gray-900 dark:text-foreground">{plan.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-muted-foreground">Amount Paid (incl. GST)</p>
                  <p className="font-medium text-gray-900 dark:text-foreground">
                    {plan.currency}{plan.price.toLocaleString('en-IN')}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-muted-foreground">Payment Date</p>
                  <p className="font-medium text-gray-900 dark:text-foreground">{new Date().toLocaleDateString('en-IN')}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-sm text-gray-600 dark:text-muted-foreground">Email</p>
                  <p className="font-medium text-gray-900 dark:text-foreground">{email}</p>
                </div>
              </div>
            </div>

            <div className="space-y-3 mb-6">
              <Card className="bg-orange-50 dark:bg-orange-900/20 border-orange-200">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-start space-x-3 text-left">
                    <CheckCircle className="w-5 h-5 text-orange-600 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-foreground text-sm mb-1">What&apos;s Next?</h3>
                      <p className="text-xs text-gray-700 dark:text-foreground">
                        A confirmation email has been sent to your registered email address. You can now login and start managing your venue bookings!
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => window.print()}
                className="flex-1"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Receipt
              </Button>
              <Button
                onClick={() => navigate('/login')}
                className="flex-1 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
              >
                Go to Login
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="mt-8 text-center">
          <p className="text-xs text-gray-500 dark:text-muted-foreground">
            Powered by <span className="font-semibold text-teal-600">MYONK</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
